import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Button, Card, Col, Empty, Popconfirm, Row, Space, Spin, Tag, Tooltip, message } from 'antd';
import {
    ArrowLeftOutlined,
    CustomerServiceOutlined,
    DeleteOutlined,
    DownloadOutlined,
    FileTextOutlined,
    PlusOutlined,
    ReadOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { getSongsThunk } from '../Feature/SongsSlice';
import { getRecordingsThunk } from '../Feature/RecordingsSlice';
import { getCurrentSpaceThunk } from '../Feature/SpacesSlice';
import { deleteLyrics, deletePartition, getLyricsDownloadUrl } from '../Feature/API';
import { FileUploadModal } from '../Components/FileUploadModal';
import { RecordingTable } from '../Components/RecordingTable';
import { PartitionDownloadButton } from '../Components/PartitionDownloadButton';
import { useAdminMode } from '../hooks/useAdminMode';
import { getPartitionFormatLabel, getPartitionStatusMeta } from '../Utils/songPartitions';
import '../Styles/ActionButtons.css';
import './SongDetailPage.css';

const formatDate = (value) => (value ? moment(value).format('D MMM YYYY, HH:mm') : 'Unknown date');

const byNewest = (a, b) => new Date(b.uploaded_at || 0) - new Date(a.uploaded_at || 0);

const LyricsDownloadButton = ({ lyrics, songId, spaceId }) => {
    const [loading, setLoading] = useState(false);

    const handleDownload = async () => {
        // Open the tab synchronously so popup blockers don't swallow it after the await.
        const tab = window.open('', '_blank');
        setLoading(true);
        try {
            const { url } = await getLyricsDownloadUrl(spaceId, songId, lyrics.id);
            if (tab) {
                tab.location.href = url;
            } else {
                window.location.assign(url);
            }
        } catch (error) {
            if (tab) tab.close();
            message.error('Failed to download lyrics: ' + (error.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Button icon={<DownloadOutlined />} loading={loading} onClick={handleDownload}>
            Download
        </Button>
    );
};

const FileRow = ({ icon, title, tags, uploadedAt, deleted, actions }) => (
    <div className={`song-file-row${deleted ? ' song-file-row--deleted' : ''}`}>
        <div className="song-file-row__icon">{icon}</div>
        <div className="song-file-row__body">
            <div className="song-file-row__title" title={title}>{title || 'Untitled file'}</div>
            <div className="song-file-row__meta">
                {tags}
                <span className="song-file-row__date">{formatDate(uploadedAt)}</span>
            </div>
        </div>
        <div className="song-file-row__actions">{actions}</div>
    </div>
);

const DeleteButton = ({ deleted, kind, onConfirm }) => (
    <Popconfirm
        title={deleted ? `Permanently delete this ${kind}?` : `Delete this ${kind}?`}
        okText="Delete"
        okButtonProps={{ danger: true }}
        onConfirm={onConfirm}
    >
        <Tooltip title="Delete">
            <Button danger type="text" icon={<DeleteOutlined />} aria-label={`Delete ${kind}`} />
        </Tooltip>
    </Popconfirm>
);

export const SongDetailPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { spaceId, songId } = useParams();
    const { adminMode } = useAdminMode();

    const currentSpace = useSelector((state) => state.spaces.current);
    const currentUser = useSelector((state) => state.users.current);
    const songs = useSelector((state) => state.songs.songs);
    const songsStatus = useSelector((state) => state.songs.status);
    const songsError = useSelector((state) => state.songs.error);
    const recordings = useSelector((state) => state.recordings.recordings);

    const [uploadModalVisible, setUploadModalVisible] = useState(false);
    const [initialLoadDone, setInitialLoadDone] = useState(false);

    const isOwner = Boolean(currentUser?.id && currentSpace?.data?.space_owner?.id === currentUser.id);
    const showDeleted = isOwner && adminMode;
    const backToSongs = () => navigate(`/spaces/${spaceId}/songs`);

    useEffect(() => {
        if (spaceId && currentSpace?.data?.id !== spaceId) {
            dispatch(getCurrentSpaceThunk(spaceId));
        }
    }, [spaceId, dispatch, currentSpace?.data?.id]);

    useEffect(() => {
        if (!spaceId) return undefined;
        let cancelled = false;
        setInitialLoadDone(false);
        Promise.allSettled([
            dispatch(getSongsThunk(spaceId)),
            dispatch(getRecordingsThunk(spaceId)),
        ]).then(() => {
            if (!cancelled) setInitialLoadDone(true);
        });
        return () => { cancelled = true; };
    }, [dispatch, spaceId]);

    const song = useMemo(() => (songs || []).find((s) => s.id === songId) || null, [songs, songId]);

    const partitions = useMemo(
        () => (song?.partitions || []).filter((p) => showDeleted || !p.is_deleted).sort(byNewest),
        [song, showDeleted]
    );
    const lyrics = useMemo(
        () => (song?.lyrics || []).filter((l) => showDeleted || !l.is_deleted).sort(byNewest),
        [song, showDeleted]
    );
    const songRecordings = useMemo(
        () => (song ? (recordings || []).filter((r) => r.song?.id === song.id) : []),
        [recordings, song]
    );

    const refreshSongs = () => dispatch(getSongsThunk(spaceId));

    const handleDelete = async (kind, id) => {
        const label = kind === 'partition' ? 'Partition' : 'Lyrics';
        try {
            message.loading({ content: `Deleting ${kind}…`, key: id });
            if (kind === 'partition') {
                await deletePartition(spaceId, id);
            } else {
                await deleteLyrics(spaceId, id);
            }
            message.success({ content: `${label} deleted`, key: id });
            refreshSongs();
        } catch (error) {
            message.error({ content: `Failed to delete ${kind}: ${error.message || 'Unknown error'}`, key: id });
        }
    };

    if (!song) {
        const stillLoading = !initialLoadDone || songsStatus === 'loading';
        return (
            <div className="song-detail">
                <Button type="link" className="song-detail__back" icon={<ArrowLeftOutlined />} onClick={backToSongs}>
                    Back to songs
                </Button>
                {stillLoading ? (
                    <div className="song-detail__loading"><Spin size="large" /></div>
                ) : songsStatus === 'failed' ? (
                    <Alert message="Unable to load song" description={songsError} type="error" showIcon />
                ) : (
                    <Empty description="Song not found" style={{ padding: 48 }} />
                )}
            </div>
        );
    }

    const refreshing = songsStatus === 'loading';

    const recordingColumns = [
        {
            title: 'Date',
            dataIndex: 'creation_date_time',
            key: 'creation_date_time',
            render: (datetime) => moment(datetime).utc().format('YYYY-MM-DD'),
            responsive: ['sm'],
        },
        {
            title: 'Labels',
            key: 'labels',
            render: (_, record) => (record.labels?.length
                ? <Space size={[4, 4]} wrap>{record.labels.map((l) => <Tag key={l.id}>{l.label_name}</Tag>)}</Space>
                : '—'),
            responsive: ['sm'],
        },
    ];

    return (
        <div className="song-detail">
            <Button type="link" className="song-detail__back" icon={<ArrowLeftOutlined />} onClick={backToSongs}>
                Back to songs
            </Button>

            <div className="song-detail__header">
                <div className="song-detail__heading">
                    <h1 className="song-detail__title">{song.song_name}</h1>
                    <Space size={[8, 8]} wrap>
                        <Tag icon={<ReadOutlined />} color="green">
                            {partitions.length} partition{partitions.length === 1 ? '' : 's'}
                        </Tag>
                        <Tag icon={<FileTextOutlined />} color="gold">
                            {lyrics.length} lyrics
                        </Tag>
                        <Tag icon={<CustomerServiceOutlined />} color="blue">
                            {songRecordings.length} recording{songRecordings.length === 1 ? '' : 's'}
                        </Tag>
                        {refreshing && <Spin size="small" />}
                    </Space>
                </div>
                {isOwner && (
                    <Button
                        className="action-button-primary action-button-large song-detail__upload"
                        icon={<PlusOutlined />}
                        onClick={() => setUploadModalVisible(true)}
                    >
                        Upload file
                    </Button>
                )}
            </div>

            <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                    <Card title={<Space><ReadOutlined />Partitions</Space>} className="song-detail__card">
                        {partitions.length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No partitions yet">
                                {isOwner && <Button onClick={() => setUploadModalVisible(true)}>Upload a partition</Button>}
                            </Empty>
                        ) : partitions.map((partition) => {
                            const status = getPartitionStatusMeta(partition);
                            return (
                                <FileRow
                                    key={partition.id}
                                    icon={<ReadOutlined />}
                                    title={partition.file_name}
                                    uploadedAt={partition.uploaded_at}
                                    deleted={partition.is_deleted}
                                    tags={<>
                                        <Tag>{getPartitionFormatLabel(partition)}</Tag>
                                        <Tag color={status.color}>{status.label}</Tag>
                                        {partition.is_deleted && <Tag color="red">Deleted</Tag>}
                                    </>}
                                    actions={<>
                                        {!partition.is_deleted && (
                                            <PartitionDownloadButton partition={partition} songId={song.id} spaceId={spaceId} />
                                        )}
                                        {isOwner && (
                                            <DeleteButton
                                                kind="partition"
                                                deleted={partition.is_deleted}
                                                onConfirm={() => handleDelete('partition', partition.id)}
                                            />
                                        )}
                                    </>}
                                />
                            );
                        })}
                    </Card>
                </Col>

                <Col xs={24} lg={12}>
                    <Card title={<Space><FileTextOutlined />Lyrics</Space>} className="song-detail__card">
                        {lyrics.length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No lyrics yet">
                                {isOwner && <Button onClick={() => setUploadModalVisible(true)}>Upload lyrics</Button>}
                            </Empty>
                        ) : lyrics.map((item) => (
                            <FileRow
                                key={item.id}
                                icon={<FileTextOutlined />}
                                title={item.file_name}
                                uploadedAt={item.uploaded_at}
                                deleted={item.is_deleted}
                                tags={<>
                                    {item.language && <Tag>{item.language.toUpperCase()}</Tag>}
                                    {item.is_deleted && <Tag color="red">Deleted</Tag>}
                                </>}
                                actions={<>
                                    {!item.is_deleted && (
                                        <LyricsDownloadButton lyrics={item} songId={song.id} spaceId={spaceId} />
                                    )}
                                    {isOwner && (
                                        <DeleteButton
                                            kind="lyrics"
                                            deleted={item.is_deleted}
                                            onConfirm={() => handleDelete('lyrics', item.id)}
                                        />
                                    )}
                                </>}
                            />
                        ))}
                    </Card>
                </Col>

                <Col xs={24}>
                    <Card title={<Space><CustomerServiceOutlined />Recordings</Space>} className="song-detail__card">
                        {songRecordings.length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No recordings for this song" />
                        ) : (
                            <RecordingTable recordings={songRecordings} columns={recordingColumns} />
                        )}
                    </Card>
                </Col>
            </Row>

            {isOwner && (
                <FileUploadModal
                    visible={uploadModalVisible}
                    onClose={() => setUploadModalVisible(false)}
                    song={song}
                    spaceId={spaceId}
                    onFileUpload={refreshSongs}
                />
            )}
        </div>
    );
};

